import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { NAV_GROUPS, PROJECT_DPR_PAGES, PROJECT_NAV_GROUPS, applyRoleFilter, applyPermissionFilter, activeTopNavId, topNavPanels } from "@/components/layout/navConfig";

/* Inside a project (and in every module listed in SIDEBAR_MODULES): a full-width sub nav bar under the top bar (in a project: Overview / Setup / Planning / DPR / Tracking & Risk / Finance;
   in a module: its sections) and a docked right side bar listing the pages of the chosen section. The workspace sits beside the bar and scrolls on its own.
   Anywhere else it renders its children untouched. */
/* Modules that use this layout: their sections become the sub nav bar, their pages the right side bar.
   Remove an id here to give a module back its old top-bar tabs. */
export const SIDEBAR_MODULES = ["masters", "client-management", "procurement", "inventory", "production", "assets-and-plant", "hr-and-payroll", "finance", "governance", "reports", "administration"];
const KEY = "buildnexa.projectSideBar.collapsed";
const readCollapsed = () => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } };

function findProjectSection(nodes) {
  for (const n of nodes || []) {
    if (n.isProjectSection) return n;
    const hit = findProjectSection(n.children) || findProjectSection(n.items);
    if (hit) return hit;
  }
  return null;
}

export function ProjectNavFrame({ children }) {
  const { projectId } = useParams();
  const { pathname } = useLocation();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();
  const [sel, setSel] = useState(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const visible = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const topId = projectId ? null : activeTopNavId(visible, pathname, projectId);

  /* every group is { id, label, icon, pages: [{ key, to, end, label, icon, testId }] } */
  const groups = useMemo(() => {
    if (projectId) {
      const section = findProjectSection(visible.flatMap((g) => g.items));
      const pages = (section?.dynamicChildren || PROJECT_DPR_PAGES).filter((p) => !section?.allowedPages || section.allowedPages.includes(p));
      return PROJECT_NAV_GROUPS.map((g) => ({
        ...g,
        pages: pages.filter((p) => g.suffixes.includes(p.suffix)).map((p) => ({ key: p.testId, to: `/projects/${projectId}/${p.suffix}`, end: p.end ?? p.suffix === "", label: p.label, icon: p.icon, testId: p.testId })),
      })).filter((g) => g.pages.length);
    }
    if (topId && SIDEBAR_MODULES.includes(topId)) {
      const leaves = (n) => (n.children?.length ? n.children.flatMap(leaves) : n.to ? [n] : []);
      const page = (l) => ({ key: l.id, to: l.to, end: l.end, label: l.label, icon: l.icon, testId: l.testId });
      /* a panel whose items have sub-items gives one section per item; plain pages are one section named after the panel */
      return topNavPanels(visible, topId).flatMap((panel) => {
        const kids = panel.items.filter((n) => n.children?.length && !n.isProjectSection);
        const plain = panel.items.filter((n) => !n.children?.length && !n.isProjectSection);
        const out = kids.map((n) => ({ id: n.id, label: n.label, icon: n.icon, pages: leaves(n).map(page) }));
        if (plain.length) out.push({ id: kids.length ? `${panel.id}.pages` : panel.id, label: panel.label, icon: panel.icon, pages: plain.flatMap(leaves).map(page) });
        return out;
      }).filter((g) => g.pages.length);
    }
    return [];
  }, [projectId, topId, visible]);

  const here = (p) => (p.end ? pathname === p.to || pathname === `${p.to}/` : pathname === p.to || pathname.startsWith(`${p.to}/`));
  const activeId = groups.find((g) => g.pages.some(here))?.id;
  useEffect(() => { setSel(null); }, [pathname]);

  if (groups.length === 0) return <>{children}</>;

  const shownId = sel || activeId || groups[0].id;
  const shown = groups.find((g) => g.id === shownId) || groups[0];
  const setCol = (v) => { setCollapsed(v); try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* ignore */ } };
  const pick = (id) => { setSel(id); if (collapsed) setCol(false); };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* sub nav bar: full width under the top bar, over the page and the right side bar */}
      <div data-testid="project-subnav" data-tour="module-sections" className="relative z-20 flex min-h-[42px] shrink-0 items-stretch gap-1 overflow-x-auto border-b border-[#4A5260] bg-[#2E353F] px-3">
        {groups.map((g) => {
          const Icon = g.icon;
          const on = g.id === shown.id;
          return (
            <button key={g.id} type="button" data-testid={`project-subnav-${g.id}`} onClick={() => pick(g.id)} aria-current={on ? "page" : undefined}
              className={cn("flex shrink-0 items-center gap-2 whitespace-nowrap border-b-[3px] px-4 text-[13px] font-medium transition-colors",
                on ? "border-[#D6657F] bg-[#910029] font-semibold text-white" : "border-transparent text-[#ECF4F7]/75 hover:bg-[#454D5C] hover:text-[#ECF4F7]")}>
              {Icon && <Icon className="h-4 w-4" />}{g.label}
            </button>
          );
        })}
      </div>

      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>

        {/* right side bar: a white card listing the pages of the chosen section */}
        <aside data-testid="project-subnav-panel" data-tour="module-pages" aria-label={`${shown.label} pages`}
          className={cn("hidden shrink-0 overflow-y-auto py-4 pr-4 transition-[width] duration-200 md:block", collapsed ? "w-[66px]" : "w-[256px]")}>
          <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
            <div className={cn("flex h-12 shrink-0 items-center border-b border-border", collapsed ? "justify-center" : "gap-2.5 px-4")}>
              {collapsed ? (
                <button type="button" onClick={() => setCol(false)} title="Expand" aria-label="Expand side bar" data-testid="project-subnav-expand" className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-primary/10 hover:text-primary"><ChevronsLeft className="h-4 w-4" /></button>
              ) : (
                <span className="flex min-w-0 items-center gap-2.5 truncate text-sm font-semibold text-foreground">{shown.icon && <shown.icon className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />}{shown.label}</span>
              )}
            </div>
            <nav className="flex flex-col gap-0.5 p-2">
              {shown.pages.map((p) => (
                <NavLink key={p.key} to={p.to} end={p.end} data-testid={p.testId} title={collapsed ? p.label : undefined}
                  className={({ isActive }) => cn("flex items-center rounded-lg text-[13.5px] transition-colors", collapsed ? "justify-center py-2.5" : "gap-3 px-3 py-2.5",
                    isActive ? "bg-primary/10 font-semibold text-primary" : "font-medium text-foreground/85 hover:bg-primary/5 hover:text-foreground")}>
                  {p.icon && <p.icon className="h-[18px] w-[18px] shrink-0" />}{!collapsed && <span className="truncate">{p.label}</span>}
                </NavLink>
              ))}
            </nav>
            {!collapsed && (
              <button type="button" onClick={() => setCol(true)} data-testid="project-subnav-collapse" className="flex h-10 shrink-0 items-center gap-2 border-t border-border px-4 text-xs text-muted-foreground hover:text-primary">
                <ChevronsRight className="h-4 w-4" />Collapse
              </button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
